import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import Image from "@Components/Image";
import VideoPlayer from "@Pages/PropertyPage/components/GalleryModal/components/VideoPlayer/VideoPlayer";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import lockBody from "@Utils/lockBody";
import unlockBody from "@Utils/unLockBody";
import PlayIcon from "@Icons/PlayIcon";
import VideoChevronLeftIcon from "@Icons/VideoChevronLeftIcon";
import ShortlistButton from "@Pages/Search/components/SearchListItem/components/ShortListButton";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import CountryFlag from "@Components/CountryFlag";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import RoomTypeCard from "@Pages/VideoPage/components/RoomTypeCard";
import ReviewCard from "../ReviewCard";
import { ReviewData, ReviewMediaItem } from "../../types";
import {
  getInitials,
  getNationalityCountryCode,
  getReviewMediaFullUrl,
  getReviewMediaThumbUrl,
  getVideoSrc,
  getVideoThumb,
  REVIEW_MEDIA_SIZES,
} from "../../utils";
import { trackReviewImageOpened } from "../../analytics";
import styles from "./reviewMediaGallery.module.scss";

/** Compact review card for the mobile gallery reviews tab */
const MobileGalleryReviewCard: React.FC<{
  review: ReviewData;
  isActive?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}> = ({ review, isActive, expanded = false, onToggleExpand }) => {
  const { t } = useTranslationHook();
  const name = review.reviewer?.name || "";
  const text = review.body?.trim() || "";
  const { rating } = review;
  const avatar = review.reviewer?.avatar || "";
  const nationality = review.reviewer?.nationality || "";
  const countryCode = getNationalityCountryCode(nationality);

  const charLimit = 200;
  const isLong = text.length > charLimit;
  const truncatedContent = isLong ? `${text.slice(0, charLimit)}..` : text;
  const displayedContent = expanded ? text : truncatedContent;

  return (
    <div
      className={formatClassNames(
        styles.mobileReviewCard,
        isActive && styles.mobileReviewCardActive,
      )}
    >
      <div className={styles.mobileReviewCardTop}>
        <div className={styles.mobileReviewCardInfo}>
          <div className={styles.mobileReviewCardAvatar}>
            {avatar ? (
              <Image
                src={avatar}
                alt={name}
                width={32}
                height={32}
                className={styles.mobileReviewCardAvatarImg}
              />
            ) : (
              <span className={styles.mobileReviewCardInitials}>{getInitials(name || "A")}</span>
            )}
            {countryCode && (
              <div className={styles.mobileReviewCardFlag}>
                <CountryFlag countryCode={countryCode} width={16} height={16} />
              </div>
            )}
          </div>
          <div className={styles.mobileReviewCardUserDetails}>
            <span className={styles.mobileReviewCardName}>{name}</span>
            {review.bookingDate && (
              <span className={styles.mobileReviewCardDate}>{review.bookingDate}</span>
            )}
          </div>
        </div>
        {rating > 0 && (
          <div className={styles.mobileReviewCardRating}>
            {rating.toFixed(1)}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path
                d="M6 1L7.545 4.13L11 4.635L8.5 7.07L9.09 10.51L6 8.885L2.91 10.51L3.5 7.07L1 4.635L4.455 4.13L6 1Z"
                fill="#ffffff"
              />
            </svg>
          </div>
        )}
      </div>
      {text && (
        <div className={styles.mobileReviewCardText}>
          {displayedContent}
          {isLong && onToggleExpand && (
            <span
              role="button"
              tabIndex={0}
              className={styles.mobileReviewCardReadMore}
              onClick={onToggleExpand}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onToggleExpand();
              }}
            >
              {" "}
              {expanded
                ? t("propertypage.reviewssection.readless")
                : t("propertypage.reviewssection.readmore")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

interface ReviewMediaGalleryProps {
  media: ReviewMediaItem[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  isDesktop?: boolean;
  reviews?: ReviewData[];
  mediaIndexMap?: Map<string, number>;
  propertyName?: string;
  propertyId?: string | number;
  /** For the `entry` property of `review_image_opened`. */
  entrySource?: "click" | "shared_url";
  /** Reports the open item to the section, which owns every review URL write. */
  onActiveMediaChange?: (reviewId: string, mediaNumber: number) => void;
}

const Media = ({
  item,
  isDesktop,
  eager,
}: {
  item: ReviewMediaItem;
  isDesktop: boolean;
  eager: boolean;
}) => {
  if (item?.category === "videos") {
    return (
      <VideoPlayer
        src={getVideoSrc(item)}
        thumbnailUrl={getVideoThumb(item)}
        isYoutube={item?.path?.includes("youtube")}
        autoPlay={false}
        fromGalleryCarousel
      />
    );
  }

  return (
    <Image
      width="100%"
      height={isDesktop ? "100%" : "90%"}
      loading={eager ? "eager" : "lazy"}
      alt={item?.userName ? `Review media by ${item.userName}` : "Review media"}
      src={getReviewMediaFullUrl(item, isDesktop)}
      className={styles.media}
    />
  );
};

const NavArrow = ({ direction, onClick }: { direction: "prev" | "next"; onClick: () => void }) => (
  <button
    type="button"
    className={formatClassNames(
      styles.navButton,
      direction === "prev" ? styles.prevButton : styles.nextButton,
    )}
    onClick={onClick}
    aria-label={direction === "prev" ? "Previous media" : "Next media"}
  >
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d={direction === "prev" ? "M10 12L6 8L10 4" : "M6 4L10 8L6 12"}
        stroke="#111928"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const ReviewMediaGallery: React.FC<ReviewMediaGalleryProps> = ({
  media,
  initialIndex = 0,
  isOpen,
  onClose,
  isDesktop = false,
  reviews = [],
  mediaIndexMap,
  propertyName = "",
  propertyId,
  entrySource = "click",
  onActiveMediaChange,
}) => {
  const total = media?.length || 0;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbRailRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const safeIndex = total ? Math.min(Math.max(currentIndex, 0), total - 1) : 0;
  const currentItem = media?.[safeIndex];

  // Build reverse lookup: for a given media index → owning review id
  const activeReviewId = useMemo(() => {
    if (!mediaIndexMap || !reviews?.length) return null;
    const entries = Array.from(mediaIndexMap.entries()).sort((a, b) => b[1] - a[1]);
    for (const [reviewId, startIdx] of entries) {
      if (safeIndex >= startIdx) return reviewId;
    }
    return null;
  }, [mediaIndexMap, reviews, safeIndex]);

  const { inventoryId, inventory, roomTypes, childrenShort, canonicalName } = useSelector(
    ({ propertyPageData }: AppState) => ({
      inventoryId: propertyPageData?.data?.id,
      inventory: propertyPageData?.data,
      roomTypes: propertyPageData?.roomTypes?.data || [],
      // children_short is always populated during SSR and reliably keyed to this
      // property, so it's a safer lookup source than the roomTypes slice (which
      // can be empty or stale after client-side navigation).
      childrenShort: propertyPageData?.data?.children_short || [],
      canonicalName: propertyPageData?.data?.canonical_name || "",
    }),
    shallowEqual,
  );

  const { t } = useTranslationHook();

  // Resolve the SPECIFIC room type for the active review, keyed on the review's
  // booking_details.room_type_id:
  //  1) If it equals a room type's id (roomTypes.data[].id), use that room type.
  //  2) Otherwise, if it equals one of a room type's active_children_with_filters
  //     ids, use that child's PARENT room type (its id + name).
  //  3) If it matches neither, there is no specific room type to point to.
  const matchedRoomType = useMemo(() => {
    const roomTypeId = currentItem?.roomTypeId;
    if (!roomTypeId) return null;

    // children_short mirrors roomTypes.data but is reliably populated during SSR.
    const source = roomTypes.length ? roomTypes : childrenShort;
    if (!source.length) return null;

    const targetId = String(roomTypeId);

    // 1) Direct room-type id match.
    const byId = source.find((rt: any) => String(rt?.id) === targetId);
    if (byId) return byId;

    // 2) Tenancy/config id match → return the parent room type.
    const byChildId = source.find((rt: any) => {
      const children = rt?.active_children_with_filters || [];
      return (
        Array.isArray(children) && children.some((child: any) => String(child?.id) === targetId)
      );
    });
    if (byChildId) return byChildId;

    // 3) No match — no specific room type.
    return null;
  }, [currentItem, roomTypes, childrenShort]);

  // Close this gallery + the reviews panel, then scroll to the specific room card.
  // The room section renders asynchronously, so poll for the target element.
  const handleViewRoom = (roomId?: string) => {
    // Fire the same "View Rooms" analytics event, tagged with the source that
    // matches the layout (preserving the original desktop/mobile distinction).
    gtagEventEmitter({
      action: "clicked_view_room",
      category: PAGE.INVENTORY,
      label: "success",
      cta_text: isDesktop ? "review_gallery_desktop" : "review_gallery",
    });

    onClose();
    window.dispatchEvent(new CustomEvent("close-reviews-panel"));
    let attempts = 0;
    const maxAttempts = 40; // ~4s at 100ms intervals
    const intervalId = setInterval(() => {
      attempts += 1;

      const roomCard = roomId && document.getElementById(`room-${roomId}`);
      if (roomCard) {
        const headerOffset = 54 + 48;
        const offsetPosition =
          roomCard.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        clearInterval(intervalId);
        return;
      }

      // The specific room card may be hidden behind "View Sold Out Rooms".
      // Room types are grouped by unit type, each with its own sold-out toggle,
      // so click every still-collapsed sold-out toggle to reveal hidden cards
      // (same approach as the Floor Plan section's "View Room").
      const soldOutToggles = document.querySelectorAll<HTMLElement>(
        '[data-testid="sold-out-rooms-toggle"][data-collapsed="true"]',
      );
      soldOutToggles.forEach((toggle) => toggle.click());

      if (attempts >= maxAttempts) {
        // Fallback: scroll to the room section if the specific card never appears.
        const section = document.getElementById("room_type");
        if (section) {
          const headerOffset = 54 + 48;
          const offsetPosition =
            section.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
        clearInterval(intervalId);
      }
    }, 100);
  };

  // Lock background scroll while open
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    lockBody();
    return () => {
      unlockBody();
    };
  }, []);

  // Close on Escape + Arrow key navigation
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setCurrentIndex((i) => Math.max(0, i - 1));
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setCurrentIndex((i) => Math.min(total - 1, i + 1));
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, total]);

  // Report the open item upward; the section owns the URL write. React runs child effects before
  // parent ones, so writing from here lands the params on the drawer's history entry, not the
  // gallery's. Covers every way an item becomes active: click, arrows, thumbnails, deep link.
  const hasReportedFirstOpen = useRef(false);
  useEffect(() => {
    const start = activeReviewId ? mediaIndexMap?.get(activeReviewId) : undefined;
    if (!activeReviewId || start === undefined) return;
    const mediaNumber = safeIndex - start + 1;
    onActiveMediaChange?.(activeReviewId, mediaNumber);
    // Only the first item of a shared-link gallery is `shared_url`; navigating is clicking.
    const entry =
      !hasReportedFirstOpen.current && entrySource === "shared_url" ? "shared_url" : "click";
    hasReportedFirstOpen.current = true;
    trackReviewImageOpened({
      propertyId: propertyId || "",
      reviewId: activeReviewId,
      img: mediaNumber,
      device: isDesktop ? "desktop" : "mobile",
      entry,
    });
  }, [activeReviewId, safeIndex, mediaIndexMap]);

  // Pause any playing video when the active item changes
  useEffect(() => {
    const videos = containerRef.current?.querySelectorAll("video");
    videos?.forEach((video) => {
      if (!video.paused) video.pause();
    });
    setReviewExpanded(false);
  }, [safeIndex]);

  // Auto-scroll thumbnail strip to keep the active thumbnail visible
  useEffect(() => {
    const rail = thumbRailRef.current;
    if (!rail) return;
    const activeThumb = rail.querySelector(`[data-thumb-index="${safeIndex}"]`) as HTMLElement;
    if (!activeThumb) return;
    activeThumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [safeIndex]);

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(total - 1, i + 1));

  if (!isOpen || total === 0) return null;

  const renderThumbnails = (variant: "desktop" | "mobile") => {
    const thumbSize = variant === "desktop" ? "galleryRailDesktop" : "galleryRailMobile";
    const { width: thumbWidth, height: thumbHeight } = REVIEW_MEDIA_SIZES[thumbSize];

    return media.map((item, index) => {
      const isThumbActive = index === safeIndex;
      return (
        <button
          key={`thumb-${item?.path || item?.base_path || index}`}
          type="button"
          data-thumb-index={index}
          className={
            variant === "desktop"
              ? formatClassNames(styles.deskThumb, isThumbActive && styles.deskThumbActive)
              : formatClassNames(styles.thumb, isThumbActive && styles.thumbActive)
          }
          onClick={() => setCurrentIndex(index)}
          aria-label={`View media ${index + 1}`}
        >
          <Image
            width={thumbWidth}
            height={thumbHeight}
            loading="lazy"
            alt=""
            src={getReviewMediaThumbUrl(item, thumbSize)}
            className={variant === "desktop" ? styles.deskThumbImg : styles.thumbImg}
          />
          {item?.category === "videos" && (
            <span className={variant === "desktop" ? styles.deskThumbPlay : styles.thumbPlay}>
              <span className={variant === "desktop" ? styles.deskThumbPlayIcon : undefined}>
                <PlayIcon width="12" height="12" fill={variant === "desktop" ? "#000" : "white"} />
              </span>
            </span>
          )}
        </button>
      );
    });
  };

  // ===== Desktop layout (VideoPage-style two-column view) =====
  if (isDesktop) {
    return (
      <div
        className={styles.deskOverlayWrapper}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div
          ref={containerRef}
          className={styles.deskOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Review media gallery"
        >
          <header className={styles.deskHeader}>
            <div className={styles.deskHeaderLeft}>
              <button
                type="button"
                className={styles.backButton}
                onClick={onClose}
                aria-label="Close gallery"
              >
                <VideoChevronLeftIcon />
              </button>
              <h2 className={styles.deskTitle}>{propertyName}</h2>
            </div>
            <div className={styles.deskHeaderRight}>
              {inventoryId && (
                <ShortlistButton
                  inventoryId={inventoryId}
                  inventory={inventory}
                  noAbosolute
                  screenName={APP_PAGE.PROPERTY}
                  className={styles.iconButton}
                />
              )}
            </div>
          </header>

          <div className={styles.deskContent}>
            <div className={styles.deskLeft}>
              <div className={styles.deskPlayerWrap}>
                <div className={styles.deskPlayer}>
                  <Media item={currentItem} isDesktop eager />
                  {total > 1 && safeIndex > 0 && <NavArrow direction="prev" onClick={goPrev} />}
                  {total > 1 && safeIndex < total - 1 && (
                    <NavArrow direction="next" onClick={goNext} />
                  )}
                </div>
              </div>

              {total > 1 && (
                <div className={styles.deskRail} ref={thumbRailRef}>
                  {renderThumbnails("desktop")}
                </div>
              )}
            </div>

            <div className={styles.deskRight}>
              {matchedRoomType && (
                <RoomTypeCard
                  roomType={matchedRoomType}
                  canonicalName={canonicalName}
                  onViewRoom={handleViewRoom}
                />
              )}
              {reviews
                .filter((review) => review.id === activeReviewId)
                .map((review) => (
                  <div key={review.id} data-review-id={review.id}>
                    <ReviewCard
                      review={review}
                      fullWidth
                      showFullBody
                      onMediaClick={(index: number) => setCurrentIndex(index)}
                      mediaStartIndex={mediaIndexMap?.get(review.id) || 0}
                      propertyId={propertyId}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Mobile layout (VideoPage-style) =====
  const hasReviews = reviews.length > 0;

  // Swipe gesture handling for media navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    if (Math.abs(diff) < threshold) return;

    if (diff > 0 && safeIndex < total - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (diff < 0 && safeIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  };

  return (
    <div className={styles.overlayWrapper} onWheel={(e) => e.stopPropagation()}>
      <div
        ref={containerRef}
        className={styles.overlay}
        role="dialog"
        aria-modal="true"
        aria-label="Review media gallery"
      >
        {/* Navbar */}
        <div className={styles.mobileNavbar}>
          <button
            type="button"
            className={styles.mobileBackButton}
            onClick={onClose}
            aria-label="Close gallery"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="#FFFFFF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span className={styles.mobileCounter}>
            {safeIndex + 1}/{total}
          </span>
          <div className={styles.mobileNavbarRight}>
            {inventoryId && (
              <ShortlistButton
                inventoryId={inventoryId}
                inventory={inventory}
                noAbosolute
                screenName={APP_PAGE.PROPERTY}
                className={styles.mobileShortlistButton}
                iconColor="white"
              />
            )}
          </div>
        </div>

        {/* Media Area */}
        <div
          className={styles.mobileMediaArea}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Media item={currentItem} isDesktop={false} eager />
          {total > 1 && safeIndex > 0 && <NavArrow direction="prev" onClick={goPrev} />}
          {total > 1 && safeIndex < total - 1 && <NavArrow direction="next" onClick={goNext} />}
        </div>

        {/* Review card: fixed space at the bottom, image adjusts above */}
        {hasReviews && (
          <div
            className={formatClassNames(
              styles.mobileBottomSheet,
              reviewExpanded && styles.mobileBottomSheetExpanded,
            )}
          >
            <div className={styles.mobileReviewsContent}>
              {reviews
                .filter((review) => review.id === activeReviewId)
                .map((review) => (
                  <MobileGalleryReviewCard
                    key={review.id}
                    review={review}
                    expanded={reviewExpanded}
                    onToggleExpand={() => setReviewExpanded(!reviewExpanded)}
                  />
                ))}
            </div>
            {/* Room type bar — only shown when the review's room type resolves to
                an existing room type. A deleted room (no id match) shows nothing. */}
            {matchedRoomType && (
              <div className={styles.mobileRoomTypeBar}>
                <span className={styles.mobileRoomTypeName}>{matchedRoomType.name}</span>
                <button
                  type="button"
                  className={styles.mobileViewRoomsButton}
                  onClick={() => handleViewRoom(matchedRoomType.id)}
                  aria-label={`View rooms - ${matchedRoomType.name}`}
                >
                  {t("propertypage.components.gallerycarousel.viewrooms")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(ReviewMediaGallery, {
  componentName: "ReviewMediaGallery",
  showForChina: true,
  showForWhiteLable: true,
});
